import React, { useState, useEffect } from 'react';
import { Copy, Check, X } from 'lucide-react';
import Toast from './Toast';
import { depositService } from '../services/depositService';
import { useTranslation } from '../i18n/LocaleContext';

// 充值弹层：先填金额建 USDT-TRC20 收款单，再展示收款地址 + 精确金额，
// 每 5s 轮询到账状态，链上监听确认入账（approved）即提示成功并关闭。
const DepositModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [pay, setPay] = useState(null);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setAnimating(true), 10);
    } else {
      setAnimating(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    setPay(null);
    setAmount('');
  };

  // 到账轮询：approved 即入账成功
  useEffect(() => {
    if (!isOpen || !pay) return undefined;
    let alive = true;
    const timer = setInterval(async () => {
      try {
        const res = await depositService.getStatus(pay.depositId);
        if (alive && res.status === 'approved') {
          clearInterval(timer);
          setToast({ message: t('depositCredited'), type: 'success' });
          setTimeout(handleClose, 1500);
        }
      } catch (error) {
        // 轮询瞬时失败忽略，下一轮再试
      }
    }, 5000);
    return () => { alive = false; clearInterval(timer); };
  }, [isOpen, pay, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const value = parseFloat(amount);
    if (!amount || !Number.isFinite(value) || value < 20) {
      setToast({ message: t('depositMinimum'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const res = await depositService.startPayment(value);
      setPay({ depositId: res.depositId, payAmount: res.payAmount, address: res.address });
    } catch (error) {
      setToast({
        message: error.errorCode === 'PAY_UNAVAILABLE' ? t('luckyPayUnavailable') : error.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, setCopied) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {visible && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${animating ? 'opacity-50' : 'opacity-0'}`}
            onClick={handleClose}
          />
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-lift flex flex-col max-h-[92vh] transform transition-transform duration-300 ease-out ${
              animating ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="text-lg font-bold text-ink">{pay ? t('depositPayTitle') : t('deposits')}</h2>
              <button
                onClick={handleClose}
                className="p-2 -mr-2 rounded-full text-ink-muted hover:bg-canvas transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {!pay ? (
                <form onSubmit={handleSubmit} className="px-4 pb-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">{t('depositAmount')}</label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full p-4 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-lg font-bold text-ink tabular-nums"
                      placeholder={t('enterAmount')}
                      min="20"
                      required
                    />
                    <p className="text-xs text-ink-muted mt-2">{t('depositLimit')}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[20, 50, 100].map((val) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setAmount(val.toString())}
                        className={`py-2.5 px-3 rounded-xl text-sm font-bold transition-colors ${
                          amount === val.toString()
                            ? 'bg-white ring-2 ring-brand-500 text-brand-600'
                            : 'bg-canvas text-ink hover:bg-gray-200'
                        }`}
                      >
                        ${val.toLocaleString()}.00
                      </button>
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('sendingRequest') : t('continue')}
                  </button>
                </form>
              ) : (
                <div className="px-4 pb-6 space-y-4">
                  <div className="rounded-xl bg-canvas p-4">
                    <p className="text-[12px] text-ink-muted">{t('luckyPayAmountLabel')}</p>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <p className="text-[24px] leading-none font-extrabold text-money tabular-nums">
                        {pay.payAmount.toFixed(3)} USDT
                      </p>
                      <button
                        onClick={() => copy(pay.payAmount.toFixed(3), setCopiedAmount)}
                        className="p-2 rounded-full text-brand-600 hover:bg-white transition-colors"
                      >
                        {copiedAmount ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-ink-muted">{t('luckyPayAmountHint')}</p>
                  </div>

                  <div className="flex justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pay.address)}`}
                      alt="QR Code"
                      className="w-[200px] h-[200px] rounded-xl"
                    />
                  </div>

                  <div className="rounded-xl border border-black/[0.06] p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-[12px] text-ink-muted">{t('luckyPayAddressLabel')}</p>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                        {t('luckyPayNetwork')}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-start justify-between gap-3">
                      <p className="break-all text-[13px] font-medium text-ink">{pay.address}</p>
                      <button
                        onClick={() => copy(pay.address, setCopiedAddr)}
                        className="shrink-0 p-2 rounded-full text-brand-600 hover:bg-canvas transition-colors"
                      >
                        {copiedAddr ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 py-1 text-ink-muted">
                    <span className="flex gap-1">
                      <span className="queue-dot h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                      <span className="queue-dot h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animationDelay: '0.15s' }} />
                      <span className="queue-dot h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animationDelay: '0.3s' }} />
                    </span>
                    <p className="text-[13px]">{t('luckyPayWaiting')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DepositModal;
